import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Hash, Sparkle } from '@phosphor-icons/react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { WarningCircle } from '@phosphor-icons/react';
import { SkuWizard } from '@/components/SkuWizard';
import { MissingSkuGroupNote } from '@/components/registration/MissingSkuGroupNote';
import { useT } from '@/i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _policyCache = null;
export const loadNumberPolicy = ({ force = false } = {}) => {
  if (!_policyCache || force) {
    _policyCache = axios.get(`${API}/sku/number-policy`)
      .then((r) => r.data)
      .catch(() => { _policyCache = null; return { registers: [], locked_prefixes: [] }; });
  }
  return _policyCache;
};
export const invalidateNumberPolicy = () => { _policyCache = null; };

/** 保存の直前に、予約した番号を正式に発番する。何も選んでいなければ null。 */
export const finalizeNumber = async (value) => {
  if (!value) return null;
  if (value.pending) {
    const r = await axios.post(`${API}/sku/issue`, value.pending, { withCredentials: true });
    return r.data.sku;
  }
  return (value.number || '').trim() || null;
};

/**
 * <NumberField> — 品番 / 資産番号（仕様 2.1 / 2.3 ＋ 採番ルールの選択 2026-06）。
 *
 * 採り方を **ユーザーが選べる**:
 *   * 共通採番（EQP/CSM）… 設備台帳が持つ 1 本のカウンター。既定。
 *   * 品番マスタのルール … 技術資産と同じピッカー。全ルールから選べ、
 *     この台帳のルールが先に出る。
 * `EQP` 接頭辞のルールは選べない（共通採番と二重のカウンターになるため）。
 * 発番は**保存時**。ダイアログを閉じただけなら番号は消費されない。
 * 既存行では現在の番号を読み取り専用で見せるだけ（書き換える入り口を作らない）。
 */
export const NumberField = ({
  label = '', register = 'equip', appCode = '', editing = false, currentNo = '',
  value, onChange, required = false, hint = '', testId = 'number-field',
  duplicateOf = null, onIssued = null, deptCode = '',
}) => {
  const { t } = useT();
  const [policy, setPolicy] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => { loadNumberPolicy().then(setPolicy); }, []);
  const row = useMemo(
    () => (policy?.registers || []).find((r) => r.register === register) || null,
    [policy, register],
  );
  const sharedPrefixes = row?.shared_prefixes || [];
  // §8.10 既定が「自分の台帳の 部署/種類 ルール」のときも、番号は共通採番から出る。
  const effectiveMode = row?.issues_from === 'shared' ? 'shared' : row?.mode;
  const mode = value?.mode || effectiveMode || (sharedPrefixes.length ? 'shared' : 'template');
  const sharedPrefix = value?.shared_prefix || sharedPrefixes[0] || '';
  // R1 · 部署で番号の記号が決まる（SNS/EQP00100）。連番は全社で1本のまま。
  const rawNext = row?.shared_next?.[sharedPrefix] || '';
  const sharedNext = deptCode && rawNext
    ? `${deptCode}/${rawNext.split('/').pop()}` : rawNext;
  const sharedRule = row?.series_templates?.[sharedPrefix]?.name || '';

  // 台帳の既定が「このルール」なら、何も触らなくても保存時にそのルールで発番する。
  useEffect(() => {
    if (editing || !row || value?.mode) return;
    if (effectiveMode === 'template' && row.template_code) {
      onChange?.({ mode: 'template', number: '', template_code: row.template_code,
        template_name: row.template_name, pending: { template_id: row.template_code, values: {} } });
    } else if (effectiveMode === 'shared' && sharedPrefixes.length) {
      onChange?.({ mode: 'shared', shared_prefix: sharedPrefixes[0] });
    }
  }, [row, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickShared = (prefix) => onChange?.({ mode: 'shared', shared_prefix: prefix });

  if (editing) {
    return (
      <div data-testid={testId}>
        <Label className="text-xs font-semibold text-zinc-600">{label || t('reg.number')}</Label>
        <div className="mt-1 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12px] text-zinc-600">
          <span className="font-mono font-semibold text-zinc-800" data-testid={`${testId}-value`}>
            {currentNo || '—'}
          </span>
          <span className="ml-2 text-zinc-400">{t('reg.number.keep')}</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <Label className="text-xs font-semibold text-zinc-600">
        {label || t('reg.number')} {required && <span className="text-rose-500">＊</span>}
      </Label>

      <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`${testId}-modes`}>
        {sharedPrefixes.map((p) => (
          <button key={p} type="button" onClick={() => pickShared(p)}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
              mode === 'shared' && sharedPrefix === p
                ? 'border-teal-600 bg-teal-600 text-white'
                : 'border-zinc-200 bg-white text-zinc-600 hover:border-teal-300'}`}
            data-testid={`${testId}-mode-shared-${p}`}>
            {t('reg.number.shared', { prefix: p })}
          </button>
        ))}
        <button type="button" onClick={() => setWizardOpen(true)}
          className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
            mode === 'template'
              ? 'border-sky-600 bg-sky-600 text-white'
              : 'border-zinc-200 bg-white text-zinc-600 hover:border-sky-300'}`}
          data-testid={`${testId}-mode-template`}>
          {t('reg.number.fromRule')}
        </button>
      </div>

      <div className="mt-1 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12px] text-zinc-600">
        {mode === 'shared' ? (
          <span className="font-mono font-semibold text-zinc-800" data-testid={`${testId}-value`}>
            {sharedNext ? t('reg.number.next', { no: sharedNext }) : t('reg.number.auto')}
            {sharedRule && (
              <span className="ml-2 font-sans text-[11px] font-normal text-zinc-500"
                data-testid={`${testId}-shared-rule`}>
                {sharedRule}
              </span>
            )}
          </span>
        ) : (
          <span data-testid={`${testId}-value`}>
            {value?.number
              ? <span className="font-mono font-semibold text-zinc-800">{value.number}</span>
              : <span className="text-zinc-500">{t('reg.number.onSaveByRule')}</span>}
            {(value?.template_name || value?.template_code) && (
              <span className="ml-2 text-zinc-500">
                {t('reg.number.rule', { rule: value.template_name || value.template_code })}
              </span>
            )}
          </span>
        )}
      </div>

      {mode === 'template' && (
        <Button type="button" variant="outline" className="h-8 mt-1.5 text-[11px]"
          onClick={() => setWizardOpen(true)} data-testid={`${testId}-open-rules`}>
          <Hash size={12} className="mr-1" />
          {value?.number ? t('reg.number.changeRule') : t('reg.number.chooseRule')}
        </Button>
      )}

      <p className="mt-1 flex items-start gap-1 text-[11px] leading-snug text-zinc-500">
        <Sparkle size={11} className="mt-0.5 shrink-0 text-zinc-400" />
        <span>{hint || t('reg.number.techsHint')}</span>
      </p>

      {value?.sku_group_missing && <MissingSkuGroupNote testId={`${testId}-no-group`} />}

      {duplicateOf && (
        <p className="mt-1 flex items-center gap-1 text-[11px] text-rose-700" data-testid={`${testId}-duplicate`}>
          <WarningCircle size={12} />
          {t('reg.number.duplicate', { owner: duplicateOf })}
        </p>
      )}

      <SkuWizard open={wizardOpen} onOpenChange={setWizardOpen} listAll
        defaultApp={appCode || row?.app_code || ''}
        deptLabel={row?.dept_label || ''}
        sharedSeries={(row?.shared_prefixes || []).map((p) => ({
          prefix: p, label: row?.series_labels?.[p] || p, next: row?.shared_next?.[p] || '',
        }))}
        currentSku={value?.number || ''} currentTemplateCode={value?.template_code || ''}
        onIssued={(sku, meta) => {
          if (meta?.from_shared) {
            // 番号は共通採番から保存時に出る（ルール側のカウンターは使わない）。
            onChange?.({ mode: 'shared', shared_prefix: meta.shared_prefix,
              template_code: meta.template_code || '', template_name: meta.template_name || '',
              sku_group_code: meta.sku_group_code || '',
              sku_group_category_id: meta.sku_group_category_id || '',
              sku_group_missing: !!meta.sku_group_missing });
            onIssued?.(sku, meta);
            return;
          }
          onChange?.({
            mode: 'template', number: sku, template_code: meta?.template_code || '',
            template_name: meta?.template_name || '', pending: meta?.pending || null,
            sku_group_code: meta?.sku_group_code || '',
            sku_group_category_id: meta?.sku_group_category_id || '',
            sku_group_missing: !!meta?.sku_group_missing,
          });
          onIssued?.(sku, meta);
          if (!meta?.pending) toast.success(`品番 ${sku} を使用します`);
        }} />
    </div>
  );
};

export default NumberField;
